import * as React from 'react';
import { useRef } from 'react';

import { translate } from '~l10n/client';
import cx from 'classnames';
import { useSetAtom } from 'jotai';

import { Container } from '~shared/components/Container/Container';
import { Divider } from '~shared/components/Divider';
import {
  IconChevron,
  IconShared,
  IconUser,
} from '~shared/components/icons/standard';
import { Spinner } from '~shared/components/Spinner';
import { useSafariRepaint } from '~teacher/hook/utils/useSafariRepaint';
import { useWorkspaceList } from '~teacher/hook/workspaces';
import { WorkspaceItem } from '~teacher/hook/workspaces/useWorkspaceItems';
import { proCheck } from '~teacher/util/proCheck';

import {
  launchDialogStepAtom,
  noBackButtonAtom,
  workspaceIdAtom,
  workspaceNameAtom,
} from '../atoms';

/**
 * Component for the choosing workspace view
 */
interface Props {
  item: Pick<WorkspaceItem, 'name' | 'id'>;
  onSelect: (name: string, id: string) => void;
}
export const WorkspaceRow = ({ item, onSelect }: Props) => {
  return (
    <Container
      row
      verticalCenter
      onClick={() => onSelect(item.name, item.id)}
      className={cx(
        'py-5 px-2 cursor-pointer text-blue-900 hocus:text-cyan-700 transition-colors'
      )}
    >
      <Container className={'mr-3 text-blue-900'}>
        {item.name === 'Personal' ? <IconUser /> : <IconShared />}
      </Container>
      <span style={{ color: 'inherit' }} className={'text-base truncate'}>
        {item.name}
      </span>
      <Container width={1} className={'items-end ml-3 text-grey-800'}>
        <IconChevron direction={'right'} />
      </Container>
    </Container>
  );
};

export const SelectWorkspace = () => {
  const setWorkspaceId = useSetAtom(workspaceIdAtom);
  const setWorkspaceName = useSetAtom(workspaceNameAtom);
  const setStep = useSetAtom(launchDialogStepAtom);
  const setNoBackButton = useSetAtom(noBackButtonAtom);

  const { data } = useWorkspaceList();

  // Attach to the scroll container that holds the SVG + text rows
  const listRef = useRef<HTMLDivElement>(null);
  // Run once when data is present (first render after async load)
  useSafariRepaint(listRef, !!data);

  if (!data) return <Spinner />;

  if (data.length === 0 || proCheck.hasPaidPlan() === false) {
    setWorkspaceId(null);
    setWorkspaceName(translate('Personal'));
    setStep('increment');
    setNoBackButton(true);
  } else {
    setNoBackButton(false);
  }

  return (
    <Container width={1} className={'overflow-auto'} ref={listRef}>
      <WorkspaceRow
        item={{ name: translate('Personal'), id: null }}
        onSelect={(name, id) => {
          setWorkspaceId(id);
          setWorkspaceName(name);
          setStep('increment');
        }}
      />
      {data.map((item) => {
        return (
          <div key={item.id}>
            <Divider />
            <WorkspaceRow
              item={item}
              onSelect={(name, id) => {
                setWorkspaceId(id);
                setWorkspaceName(name);
                setStep('increment');
              }}
            />
          </div>
        );
      })}
    </Container>
  );
};
